import { useEffect, useRef } from "react";

const DURATION = 13500;
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (a, b, t) => {
  const x = clamp01((t - a) / (b - a));
  return x * x * (3 - 2 * x);
};
const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const lerp = (a, b, t) => a + (b - a) * t;

const NODES = [
  { id: "RG40", x: 250, y: 205 }, { id: "RG41", x: 420, y: 140 }, { id: "RG42", x: 575, y: 215 },
  { id: "RG45", x: 735, y: 150 }, { id: "GU47", x: 880, y: 255 }, { id: "SL5", x: 150, y: 345 },
  { id: "SW18", x: 395, y: 335 }, { id: "KT13", x: 850, y: 410 }, { id: "OX10", x: 95, y: 520 },
  { id: "HP9", x: 945, y: 560 },
];
const EDGES = [[0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [0, 6], [6, 2], [4, 7], [5, 8], [7, 9], [6, 1]];
const HOUSES = [
  { x: 215, y: 610, s: 0.78 }, { x: 330, y: 500, s: 0.6 }, { x: 470, y: 655, s: 0.9 },
  { x: 805, y: 620, s: 0.85 }, { x: 905, y: 455, s: 0.58 }, { x: 110, y: 420, s: 0.5 },
];
const TARGET = { x: 645, y: 560, s: 1.45 };
const LETTERBOX = { x: TARGET.x, y: TARGET.y + 12 * TARGET.s };
const PATH_D = `M 70 110 C 200 40, 300 250, 420 240 S 560 300, 590 400 S 610 ${LETTERBOX.y - 90}, ${LETTERBOX.x} ${LETTERBOX.y - 46}`;

export function stageForT(t) {
  if (t < 0.18) return 0;
  if (t < 0.44) return 1;
  if (t < 0.63) return 2;
  if (t < 0.75) return 3;
  return 4;
}

function HouseShape({ glow = false }) {
  return (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M-36 -16 L0 -48 L36 -16" />
      <path d="M-29 -21 V24 H29 V-21" />
      <path d="M15 -34 V-46 H23 V-27" />
      <path d="M-9 24 V2 H9 V24" />
      <rect x="-23" y="-10" width="9" height="9" rx="1" />
      <rect x="14" y="-10" width="9" height="9" rx="1" />
      {glow && <rect x="-5" y="10" width="10" height="3" rx="1.2" />}
    </g>
  );
}

function Envelope() {
  return (
    <g>
      <rect x="-50" y="-32" width="100" height="64" rx="5" fill="url(#hs-env)" />
      <rect x="-50" y="-32" width="100" height="64" rx="5" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="0.8" />
      <path d="M-50 -28 L0 6 L50 -28" fill="none" stroke="rgba(18,41,15,0.2)" strokeWidth="1.4" />
      <path d="M-50 32 L-12 2 M50 32 L12 2" fill="none" stroke="rgba(18,41,15,0.09)" strokeWidth="1" />
      <circle cx="0" cy="6" r="6.5" fill="#96C11F" />
      <circle cx="0" cy="6" r="3" fill="#C6EE5A" />
      <rect x="27" y="-25" width="15" height="11" rx="1.5" fill="none" stroke="#7AA818" strokeWidth="1.2" />
    </g>
  );
}

export function HeroScene({ onStage, reduce = false }) {
  const root = useRef(null);
  const pathRef = useRef(null);
  const trailRef = useRef(null);
  const envRef = useRef(null);
  const shadowRef = useRef(null);
  const ringRef = useRef(null);
  const glowRef = useRef(null);
  const opportunityRef = useRef(null);
  const labelRefs = useRef([]);
  const nodeRefs = useRef([]);
  const radiateRefs = useRef([]);
  const dotRefs = useRef([]);
  const houseGlowRefs = useRef([]);
  const stageRef = useRef(-1);
  const onStageRef = useRef(onStage);
  onStageRef.current = onStage;

  useEffect(() => {
    const path = pathRef.current;
    const L = path.getTotalLength();
    trailRef.current.style.strokeDasharray = `${L}`;

    const render = (t) => {
      const travel = easeInOut(clamp01((t - 0.04) / 0.58));
      const p = path.getPointAtLength(L * travel);
      const q = path.getPointAtLength(Math.min(L, L * travel + 3));
      const angle = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;
      const insert = smooth(0.62, 0.71, t);
      const appear = smooth(0, 0.05, t);
      const scale = (0.52 + 0.42 * Math.sin(Math.PI * Math.min(travel, 0.92)) + 0.18 * travel) * (1 - 0.86 * insert);
      const rot = angle * 0.1 * (1 - insert) + Math.sin(t * Math.PI * 6) * 2.5 * (1 - insert);
      const x = lerp(p.x, LETTERBOX.x, insert);
      const y = lerp(p.y, LETTERBOX.y, insert);
      const op = appear * (1 - smooth(0.69, 0.72, t));
      envRef.current.setAttribute("transform", `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${rot.toFixed(2)}) scale(${scale.toFixed(3)})`);
      envRef.current.style.opacity = op;
      shadowRef.current.setAttribute("transform", `translate(${x.toFixed(1)} ${(y + 70 * scale).toFixed(1)}) scale(${scale.toFixed(3)})`);
      shadowRef.current.style.opacity = op * 0.5 * (1 - insert);

      trailRef.current.style.strokeDashoffset = `${L * (1 - travel)}`;
      trailRef.current.style.opacity = 0.85 * (1 - smooth(0.82, 0.97, t));

      NODES.forEach((n, i) => {
        const d = Math.hypot(n.x - p.x, n.y - p.y);
        const hl = t < 0.66 ? clamp01(1 - d / 150) : 0;
        if (labelRefs.current[i]) labelRefs.current[i].style.opacity = hl;
        if (nodeRefs.current[i]) nodeRefs.current[i].setAttribute("r", (2.4 + 3.2 * hl).toFixed(2));
      });

      const ringOp = smooth(0.2, 0.32, t) * (1 - smooth(0.88, 0.98, t)) * 0.75;
      ringRef.current.setAttribute("r", lerp(250, 74, smooth(0.28, 0.64, t)).toFixed(1));
      ringRef.current.style.opacity = ringOp;

      const glow = smooth(0.55, 0.64, t) * (1 - smooth(0.93, 1, t));
      glowRef.current.style.opacity = glow;

      radiateRefs.current.forEach((el, i) => {
        if (!el) return;
        const local = clamp01((t - 0.71 - i * 0.035) / 0.2);
        el.setAttribute("r", (18 + local * 320).toFixed(1));
        el.style.opacity = local > 0 ? (1 - local) * 0.55 : 0;
      });

      HOUSES.forEach((h, k) => {
        const local = smooth(0.73 + k * 0.013, 0.85 + k * 0.013, t);
        const dot = dotRefs.current[k];
        if (dot) {
          dot.setAttribute("cx", lerp(TARGET.x, h.x, local).toFixed(1));
          dot.setAttribute("cy", lerp(TARGET.y - 10, h.y - 6, local).toFixed(1));
          dot.style.opacity = local > 0.001 && local < 0.999 ? 1 : 0;
        }
        const hg = houseGlowRefs.current[k];
        if (hg) hg.style.opacity = smooth(0.84 + k * 0.013, 0.88 + k * 0.013, t) * (1 - smooth(0.93, 0.99, t));
      });

      const oppOp = smooth(0.76, 0.8, t) * (1 - smooth(0.93, 0.99, t));
      opportunityRef.current.style.opacity = oppOp;
      opportunityRef.current.setAttribute("transform", `translate(${TARGET.x} ${(TARGET.y - 112 - 10 * oppOp).toFixed(1)})`);

      const stage = stageForT(t);
      if (stage !== stageRef.current) {
        stageRef.current = stage;
        onStageRef.current?.(stage);
      }
    };

    if (reduce) {
      render(0.8);
      return undefined;
    }

    let raf = null;
    let visible = true;
    const start = performance.now();
    const frame = (now) => {
      raf = null;
      if (!visible || document.hidden) return;
      render(((now - start) % DURATION) / DURATION);
      raf = requestAnimationFrame(frame);
    };
    const resume = () => {
      if (visible && !document.hidden && raf === null) raf = requestAnimationFrame(frame);
    };
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      resume();
    }, { threshold: 0.02 });
    io.observe(root.current);
    document.addEventListener("visibilitychange", resume);
    resume();
    return () => {
      if (raf) cancelAnimationFrame(raf);
      io.disconnect();
      document.removeEventListener("visibilitychange", resume);
    };
  }, [reduce]);

  return (
    <svg ref={root} viewBox="0 0 1000 760" className="h-full w-full" preserveAspectRatio="xMidYMid meet" role="img"
      aria-label="Animated illustration: a letter travels across postcode districts to a home, which then reveals a wider homeowner audience.">
      <defs>
        <linearGradient id="hs-env" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#E9EDE1" />
        </linearGradient>
        <radialGradient id="hs-shadow">
          <stop offset="0" stopColor="rgba(0,0,0,0.55)" />
          <stop offset="1" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
        <radialGradient id="hs-glow">
          <stop offset="0" stopColor="rgba(198,238,90,0.95)" />
          <stop offset="0.4" stopColor="rgba(150,193,31,0.35)" />
          <stop offset="1" stopColor="rgba(150,193,31,0)" />
        </radialGradient>
        <linearGradient id="hs-trail" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="rgba(198,238,90,0)" />
          <stop offset="0.5" stopColor="rgba(198,238,90,0.55)" />
          <stop offset="1" stopColor="rgba(198,238,90,0.95)" />
        </linearGradient>
      </defs>

      <g stroke="rgba(255,255,255,0.07)" strokeWidth="1">
        {EDGES.map(([a, b]) => (
          <line key={`${a}-${b}`} x1={NODES[a].x} y1={NODES[a].y} x2={NODES[b].x} y2={NODES[b].y} />
        ))}
      </g>

      {NODES.map((n, i) => (
        <g key={n.id} className="font-ui">
          <circle ref={(el) => (nodeRefs.current[i] = el)} cx={n.x} cy={n.y} r="2.4" fill="#C6EE5A" opacity="0.75" />
          <circle cx={n.x} cy={n.y} r="9" fill="none" stroke="rgba(255,255,255,0.08)" />
          <text x={n.x + 14} y={n.y + 4} fontSize="12" letterSpacing="1.4" fill="rgba(255,255,255,0.36)" fontWeight="600">{n.id}</text>
          <text ref={(el) => (labelRefs.current[i] = el)} x={n.x + 14} y={n.y + 4} fontSize="12" letterSpacing="1.4" fill="#C6EE5A" fontWeight="600" style={{ opacity: 0 }}>{n.id}</text>
        </g>
      ))}

      <path d={PATH_D} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.2" strokeDasharray="2 7" />
      <path ref={pathRef} d={PATH_D} fill="none" stroke="none" />
      <path ref={trailRef} d={PATH_D} fill="none" stroke="url(#hs-trail)" strokeWidth="1.6" strokeLinecap="round" />

      {HOUSES.map((h, k) => (
        <g key={k} transform={`translate(${h.x} ${h.y}) scale(${h.s})`}>
          <g stroke="rgba(255,255,255,0.32)" strokeWidth={1.4 / h.s}>
            <HouseShape />
          </g>
          <g ref={(el) => (houseGlowRefs.current[k] = el)} stroke="#C6EE5A" strokeWidth={1.8 / h.s} style={{ opacity: 0 }}>
            <HouseShape />
          </g>
        </g>
      ))}

      <circle ref={ringRef} cx={TARGET.x} cy={TARGET.y} r="250" fill="none" stroke="rgba(198,238,90,0.7)" strokeWidth="1" strokeDasharray="3 9" style={{ opacity: 0 }} />
      <g style={{ transformOrigin: `${TARGET.x}px ${TARGET.y}px` }} className="animate-spin-slow">
        <circle cx={TARGET.x} cy={TARGET.y} r="118" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="1 14" />
      </g>

      {[0, 1, 2].map((i) => (
        <circle key={i} ref={(el) => (radiateRefs.current[i] = el)} cx={TARGET.x} cy={TARGET.y} r="18" fill="none" stroke="#96C11F" strokeWidth="1.2" style={{ opacity: 0 }} />
      ))}
      {HOUSES.map((_, k) => (
        <circle key={k} ref={(el) => (dotRefs.current[k] = el)} cx={TARGET.x} cy={TARGET.y} r="3.2" fill="#C6EE5A" style={{ opacity: 0 }} />
      ))}

      <g transform={`translate(${TARGET.x} ${TARGET.y}) scale(${TARGET.s})`}>
        <g stroke="rgba(255,255,255,0.75)" strokeWidth={1.3 / TARGET.s}>
          <HouseShape glow />
        </g>
      </g>
      <g ref={glowRef} style={{ opacity: 0 }}>
        <circle cx={LETTERBOX.x} cy={LETTERBOX.y + 2} r="46" fill="url(#hs-glow)" />
        <rect x={LETTERBOX.x - 7.5} y={LETTERBOX.y} width="15" height="4.5" rx="1.6" fill="#C6EE5A" />
      </g>

      <ellipse ref={shadowRef} cx="0" cy="0" rx="44" ry="7" fill="url(#hs-shadow)" style={{ opacity: 0 }} />
      <g ref={envRef} style={{ opacity: 0 }}>
        <Envelope />
      </g>

      <g ref={opportunityRef} style={{ opacity: 0 }} className="font-ui">
        <rect x="-92" y="-16" width="184" height="30" rx="15" fill="rgba(18,41,15,0.85)" stroke="rgba(198,238,90,0.6)" />
        <circle cx="-74" cy="-1" r="3.5" fill="#C6EE5A" />
        <text x="6" y="3.5" textAnchor="middle" fontSize="11" letterSpacing="1.6" fontWeight="600" fill="#FFFFFF">VALUATION OPPORTUNITY</text>
      </g>
    </svg>
  );
}

// Lightweight mobile / reduced-motion version.
export function HeroSceneSimple() {
  return (
    <svg viewBox="0 0 400 300" className="h-full w-full" role="img" aria-label="Illustration: a letter arriving at a home's letterbox.">
      <defs>
        <linearGradient id="hss-env" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#E9EDE1" />
        </linearGradient>
        <radialGradient id="hss-glow">
          <stop offset="0" stopColor="rgba(198,238,90,0.9)" />
          <stop offset="1" stopColor="rgba(150,193,31,0)" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="190" r="110" fill="none" stroke="rgba(198,238,90,0.35)" strokeDasharray="3 9" />
      <g transform="translate(200 200) scale(1.6)" stroke="rgba(255,255,255,0.8)" strokeWidth="0.9">
        <HouseShape glow />
      </g>
      <circle cx="200" cy="221" r="30" fill="url(#hss-glow)" className="animate-blink" />
      <g className="animate-float" style={{ transformBox: "fill-box" }}>
        <g transform="translate(200 82) rotate(-6) scale(0.9)">
          <rect x="-50" y="-32" width="100" height="64" rx="5" fill="url(#hss-env)" />
          <path d="M-50 -28 L0 6 L50 -28" fill="none" stroke="rgba(18,41,15,0.2)" strokeWidth="1.4" />
          <circle cx="0" cy="6" r="6.5" fill="#96C11F" />
        </g>
      </g>
      {[["RG40", 40, 60], ["RG41", 300, 40], ["SW18", 330, 250], ["GU47", 30, 250]].map(([t, x, y]) => (
        <text key={t} x={x} y={y} fontSize="11" letterSpacing="1.4" fill="rgba(255,255,255,0.4)" className="font-ui" fontWeight="600">{t}</text>
      ))}
    </svg>
  );
}
